// src/components/common/address/addressValidators.js
import i18n from '../../../i18n';
import { findCountryByName, getCityLabelSync, getStateOptions, prefetchCityI18n } from '../../../utils/geo';
import { getUiLocaleTag } from '../../../utils/regionalFormat';

/** Returns true if any address field is provided. */
export const hasAnyAddress = (addr) => {
  if (!addr) return false;
  const {
    addressLine1,
    addressLine2,
    city,
    state,
    pincode,
    latitude,
    longitude,
  } = addr;
  // Country alone (e.g. regional default pre-fill) does not count as starting an address
  return Boolean(
    (addressLine1 && addressLine1.trim()) ||
    (addressLine2 && addressLine2.trim()) ||
    (city && city.trim()) ||
    (state && state.trim()) ||
    (pincode && pincode.trim()) ||
    (latitude && String(latitude).trim()) ||
    (longitude && String(longitude).trim()),
  );
};

/**
 * Validates address when any address field is given.
 * Returns { ok: boolean, message?: string }
 */
export const validateAddressIfPresent = (addr) => {
  if (!hasAnyAddress(addr)) return { ok: true };
  const { country, city, latitude, longitude } = addr;

  if (!country?.trim() || !city?.trim()) {
    return {
      ok: false,
      message: i18n.t('forms.address.countryCityRequired', {
        defaultValue: 'If you enter an address, please fill at least Country and City.',
      }),
    };
  }
  if (latitude && isNaN(parseFloat(latitude))) {
    return {
      ok: false,
      message: i18n.t('forms.validation.invalidLatitude', {
        defaultValue: 'Latitude must be a valid number.',
      }),
    };
  }
  if (longitude && isNaN(parseFloat(longitude))) {
    return {
      ok: false,
      message: i18n.t('forms.validation.invalidLongitude', {
        defaultValue: 'Longitude must be a valid number.',
      }),
    };
  }
  return { ok: true };
};

/** Lines to render on profile/detail pages (skips empty parts). Country/state/city labels follow UI language. */
export const formatAddressLines = (addr) => {
  if (!hasAnyAddress(addr)) return [];
  const lines = [];
  if (addr.addressLine1?.trim()) lines.push(addr.addressLine1.trim());
  if (addr.addressLine2?.trim()) lines.push(addr.addressLine2.trim());

  const country = addr.country?.trim() ? findCountryByName(addr.country) : null;
  if (country && addr.city?.trim()) {
    // Fire-and-forget warm of city-i18n cache for subsequent renders.
    prefetchCityI18n(addr.country);
  }
  const stateOpt = country && addr.state?.trim()
    ? getStateOptions(addr.country).find((s) => s.name === addr.state.trim() || s.label === addr.state.trim())
    : null;
  const city = country && addr.city?.trim()
    ? getCityLabelSync(addr.country, addr.city)
    : (addr.city?.trim() || '');
  const stateLabel = stateOpt?.label || addr.state?.trim() || '';
  const cityState = [city, stateLabel].filter(Boolean).join(", ");
  const pin = addr.pincode?.trim();
  if (cityState && pin) lines.push(`${cityState} - ${pin}`);
  else if (cityState) lines.push(cityState);
  else if (pin) lines.push(pin);

  if (country) {
    const locale = getUiLocaleTag();
    try {
      const label = typeof Intl !== 'undefined' && Intl.DisplayNames
        ? new Intl.DisplayNames([locale], { type: 'region' }).of(country.isoCode)
        : null;
      lines.push(label || country.name);
    } catch {
      lines.push(country.name);
    }
  } else if (addr.country?.trim()) {
    lines.push(addr.country.trim());
  }
  return lines;
};

/** API payload — omits country-only pre-fill when user did not enter an address. */
export const addressPayloadFields = (addr, { addressId = null } = {}) => {
  if (!hasAnyAddress(addr)) {
    return {
      addressId: null,
      addressLine1: null,
      addressLine2: null,
      city: null,
      state: null,
      country: null,
      pincode: null,
      latitude: null,
      longitude: null,
    };
  }
  return {
    addressId,
    addressLine1: addr.addressLine1?.trim() || null,
    addressLine2: addr.addressLine2?.trim() || null,
    city: addr.city?.trim() || null,
    state: addr.state?.trim() || null,
    country: addr.country?.trim() || null,
    pincode: addr.pincode?.trim() || null,
    latitude: addr.latitude != null && String(addr.latitude).trim() !== ""
      ? parseFloat(addr.latitude)
      : null,
    longitude: addr.longitude != null && String(addr.longitude).trim() !== ""
      ? parseFloat(addr.longitude)
      : null,
  };
};
